import Image, { ImageProps } from 'next/image';
import OptimizedImage from './OptimizedImage';
import { getImageStrategy } from '@/utils/imageStrategy';
import { CSSProperties } from 'react';

interface SmartImageProps extends Omit<ImageProps, 'priority' | 'loading'> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
  // Strategy context
  isAboveFold?: boolean;
  isLCP?: boolean;
  isCritical?: boolean;
  itemCount?: number;
  index?: number;
  // Image type için sizes optimizasyonu
  imageType?:
    | 'hero'
    | 'hero-desktop'
    | 'hero-mobile'
    | 'product'
    | 'icon'
    | 'banner'
    | 'banner-large'
    | 'sub-banner'
    | 'thumbnail'
    | 'magnify'
    | 'payment-options'
    | 'custom';
  // Image props - manual override
  priority?: boolean;
  loading?: 'eager' | 'lazy';
  // Other Next.js Image props
  fill?: boolean;
  sizes?: string;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  unoptimized?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  onLoadingComplete?: () => void;
  fetchPriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
}

// ✅ MOBILE-FIRST SIZES CALCULATION - DÜZELTİLDİ
function getOptimizedSizes(
  imageType: SmartImageProps['imageType'],
  isLCPCandidate: boolean | undefined,
  displayWidth?: number,
  fill?: boolean,
): string {
  const isLCP = Boolean(isLCPCandidate);

  switch (imageType) {
    case 'hero':
      // LCP'de yüksek kalite, ama mobilde daha düşük px
      return isLCP
        ? '(max-width: 480px) 360px, (max-width: 768px) 600px, 1200px'
        : '(max-width: 480px) 320px, (max-width: 768px) 500px, 800px';

    case 'hero-desktop':
      // Do not load on mobile; on desktop use container width (~100vw up to 1200-1440)
      return ['(max-width:1023px) 0px', '(min-width:1024px) 100vw'].join(', ');

    case 'hero-mobile':
      // PSI-friendly fixed px per breakpoint (prevents over-fetching 640/1024 on 390px screens)
      return [
        '(min-width:1024px) 0px',
        '(max-width:360px) 360px',
        '(max-width:390px) 390px',
        '(max-width:428px) 412px',
        '412px',
      ].join(', ');

    case 'banner':
      // ✅ CRITICAL FIX: Küçük mobil bannerlar için
      return '(max-width: 430px) 100vw, 430px';

    case 'banner-large':
      // ✅ CRITICAL FIX: Desktop bannerlar için (max 1200px)
      return '(max-width: 480px) 430px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1200px';

    case 'sub-banner':
      // ✅ CRITICAL FIX: Küçük bannerlar için doğru boyutlar
      return '(max-width: 640px) 110px, (max-width: 768px) 140px, (max-width: 1024px) 160px, (max-width: 1280px) 180px, 200px';

    case 'product':
      // ✅ Daha düşük mobil boyutlar - DPR çarpınca bile aşırı büyük olmaz
      return '(max-width: 480px) 130px, (max-width: 768px) 160px, (max-width: 1024px) 214px, 214px';

    case 'thumbnail':
      return '(max-width: 480px) 100px, (max-width: 768px) 150px, 150px';

    case 'magnify':
      return '(max-width: 640px) 100vw, 600px';

    case 'payment-options':
      return '(max-width: 768px) 100vw, 676px';

    case 'icon':
      // ✅ Icons are always small
      const iconSize = displayWidth || 48;
      return `${iconSize}px`;

    case 'custom':
      if (displayWidth) {
        return `(max-width: ${displayWidth}px) 100vw, ${displayWidth}px`;
      }
      return '(max-width: 480px) 100vw, 400px';

    default:
      // ✅ Conservative fallback
      if (fill) {
        return '(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1200px';
      } else {
        const actualWidth = displayWidth || 400;
        return `(max-width: ${actualWidth}px) 100vw, ${actualWidth}px`;
      }
  }
}

export default function SmartImage({
  src,
  alt,
  width,
  height,
  className = '',
  style,
  isAboveFold = false,
  isLCP = false,
  isCritical = false,
  itemCount,
  index,
  imageType,
  fill = false,
  sizes: customSizes,
  // Manual overrides
  priority: manualPriority,
  loading: manualLoading,
  // Other props
  quality,
  placeholder = 'empty',
  blurDataURL,
  unoptimized = false,
  onLoad,
  onError,
  onLoadingComplete,
  fetchPriority,
  decoding,
  ...props
}: SmartImageProps) {
  const strategy = getImageStrategy({
    isAboveFold,
    isLCP,
    isCritical,
    itemCount,
    index,
  });

  // LCP Detection
  const isLCPCandidate = isLCP || isCritical || manualPriority;

  // Manual override varsa kullan, yoksa LCP detection'a göre karar ver
  const finalPriority =
    manualPriority !== undefined ? manualPriority : isLCPCandidate || strategy.priority;

  let finalLoading = manualLoading || (isLCPCandidate ? 'eager' : strategy.loading);

  // Priority true ise loading kesinlikle eager olmalı
  if (finalPriority) {
    finalLoading = 'eager';
  }

  // ✅ MOBILE-OPTIMIZED SIZES
  const optimizedSizes = customSizes || getOptimizedSizes(imageType, isLCPCandidate, width, fill);

  // ✅ Quality optimization
  const finalQuality = Math.max(
    85,
    typeof quality === 'number' ? quality : isLCPCandidate ? 85 : 85,
  );

  // Base props with proper types
  const baseProps = {
    src,
    alt,
    className,
    style,
    fill,
    sizes: optimizedSizes,
    quality: finalQuality,
    placeholder: isLCPCandidate ? 'empty' : placeholder,
    blurDataURL: isLCPCandidate ? undefined : blurDataURL,
    unoptimized,
    onLoad,
    onError,
    onLoadingComplete,
    // ✅ fetchPriority ve decoding ekle
    ...(isLCPCandidate && {
      fetchPriority: fetchPriority || ('high' as const),
      decoding: decoding || ('auto' as const),
    }),
    ...props,
  };

  // Conditional props based on fill
  const dimensionProps = fill ? {} : { width: width || 400, height: height || 300 };

  // ✅ LCP için direkt Image component kullan
  if (isLCPCandidate) {
    return (
      <Image
        {...baseProps}
        {...dimensionProps}
        priority={true}
        loading="eager"
        alt={alt}
        onLoadStart={() => {
          if (typeof window !== 'undefined') {
            performance.mark?.(`image-load-start-${src}`);
          }
        }}
      />
    );
  }

  // ✅ Normal strategy-based handling
  if (strategy.component === 'OptimizedImage') {
    return <OptimizedImage {...baseProps} {...dimensionProps} priority={finalPriority} />;
  }

  return (
    <Image
      {...baseProps}
      {...dimensionProps}
      priority={finalPriority}
      loading={finalLoading}
      alt={alt}
    />
  );
}
